<template>
  <b-card>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="card-title mb-0">
        Pre-Review Task Log
      </h4>
      <div class="text-right">
        <small class="text-muted d-block">Total: {{ totalTasks }} tasks</small>
        <small
          v-if="previewAdvertiserId"
          class="text-info d-block"
        >
          Advertiser: {{ previewAdvertiserId }}
        </small>
      </div>
    </div>

    <b-table
      :items="tasks"
      :fields="tableFields"
      :busy="loading"
      :tbody-tr-class="getRowClass"
      responsive
      hover
      show-empty
    >
      <!-- TikTok Request ID -->
      <template #cell(tiktokRequestId)="{ item }">
        <b-button
          v-if="item.tiktokRequestId"
          variant="link"
          class="p-0 text-primary font-weight-semibold"
          @click="$emit('show-task-details', item)"
        >
          <code>{{ item.tiktokRequestId }}</code>
        </b-button>
        <small
          v-else
          class="text-muted"
        >
          N/A
        </small>
      </template>

      <!-- Status -->
      <template #cell(status)="{ item }">
        <b-badge
          :variant="getTaskStatusVariant(item.status)"
          class="text-capitalize"
        >
          <feather-icon
            :icon="getTaskStatusIcon(item.status)"
            size="12"
            class="mr-25"
          />
          {{ getTaskStatusText(item.status) }}
        </b-badge>
      </template>

      <!-- Created Date -->
      <template #cell(createdAt)="{ item }">
        <div>
          <div class="font-weight-semibold">
            {{ formatDate(item.createdAt) }}
          </div>
          <small class="text-muted">
            {{ formatTime(item.createdAt) }}
          </small>
        </div>
      </template>

      <!-- Materials & Creatives -->
      <template #cell(materials)="{ item }">
        <div>
          <!-- Materials Summary -->
          <div
            v-if="item.materials && item.materials.length > 0"
            class="materials-summary"
          >
            <div class="font-weight-semibold mb-1">
              {{ item.materials.length }} material{{ item.materials.length === 1 ? '' : 's' }}:
            </div>
            <div
              v-for="(summary, index) in getMaterialsSummary(item.materials)"
              :key="index"
              class="material-line"
            >
              <span class="material-count">{{ summary.count }} {{ summary.type }}:</span>
              <b-badge
                :variant="getTaskStatusVariant(summary.status)"
                class="ml-1"
              >
                {{ getTaskStatusText(summary.status) }}
              </b-badge>
            </div>
          </div>
          <small
            v-else
            class="text-muted"
          >
            No materials
          </small>
        </div>
      </template>

      <!-- Actions -->
      <template #cell(actions)="{ item }">
        <div class="d-flex">
          <b-button
            variant="outline-primary"
            size="sm"
            @click="$emit('show-task-details', item)"
          >
            <feather-icon
              icon="EyeIcon"
              size="14"
            />
          </b-button>
          <b-button
            v-if="item.status === 'processing'"
            variant="outline-warning"
            size="sm"
            class="ml-50"
            @click="$emit('refresh-task-status', item)"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="14"
            />
          </b-button>
        </div>
      </template>

      <!-- Empty State -->
      <template #empty>
        <div class="text-center py-4">
          <feather-icon
            icon="InboxIcon"
            size="48"
            class="text-muted mb-2"
          />
          <h5 class="text-muted">
            No Tasks Found
          </h5>
          <p class="text-muted">
            No pre-review tasks have been created yet.
          </p>
        </div>
      </template>

      <!-- Loading State -->
      <template #table-busy>
        <div class="text-center my-2">
          <b-spinner class="align-middle mr-2" />
          <span>Loading task history...</span>
        </div>
      </template>
    </b-table>

    <!-- Pagination -->
    <b-pagination
      v-if="totalPages > 1"
      :value="currentPage"
      :total-rows="totalTasks"
      :per-page="pageSize"
      :limit="7"
      align="center"
      class="mt-4 pagination-lg"
      @change="$emit('page-change', $event)"
    />
  </b-card>
</template>

<script>
import {
  BButton,
  BCard,
  BTable,
  BBadge,
  BSpinner,
  BPagination,
} from 'bootstrap-vue'
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue'

export default {
  name: 'TaskLogTable',
  components: {
    BButton,
    BCard,
    BTable,
    BBadge,
    BSpinner,
    BPagination,
    FeatherIcon,
  },
  props: {
    tasks: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    totalTasks: {
      type: Number,
      default: 0,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    pageSize: {
      type: Number,
      default: 10,
    },
    previewAdvertiserId: {
      type: String,
      default: '',
    },
    highlightTaskId: {
      type: String,
      default: null,
    },
  },
  computed: {
    totalPages() {
      return Math.ceil(this.totalTasks / this.pageSize)
    },
    tableFields() {
      return [
        {
          key: 'tiktokRequestId',
          label: 'TikTok Task ID',
          sortable: true,
        },
        {
          key: 'status',
          label: 'Status',
          sortable: true,
        },
        {
          key: 'materials',
          label: 'Materials',
          sortable: false,
        },
        {
          key: 'createdAt',
          label: 'Created',
          sortable: true,
        },
        {
          key: 'actions',
          label: 'Actions',
          sortable: false,
        },
      ]
    },
  },
  methods: {
    getRowClass(item) {
      // Highlight the row if it matches the task ID from query param
      if (this.highlightTaskId && item && item.taskId === this.highlightTaskId) {
        return 'table-warning font-weight-bold'
      }
      return null
    },
    getTaskStatusText(status) {
      const statusMap = {
        pending: 'Pending',
        processing: 'In Progress',
        completed: 'Completed',
        approved: 'Approved',
        rejected: 'Rejected',
        failed: 'Failed',
        // Pre-review material statuses
        APPROVED: 'Approved',
        REJECTED: 'Rejected',
        UNSURE: 'Unsure',
        UNAVAILABLE: 'Unavailable',
        // Lowercase versions
        unsure: 'Unsure',
        unavailable: 'Unavailable',
      }
      return statusMap[status] || status
    },
    getTaskStatusVariant(status) {
      const variantMap = {
        pending: 'light-warning',
        processing: 'light-info',
        completed: 'light-success',
        approved: 'light-success',
        rejected: 'light-danger',
        failed: 'light-danger',
        // Pre-review material statuses
        APPROVED: 'light-success',
        REJECTED: 'light-danger',
        UNSURE: 'light-warning',
        UNAVAILABLE: 'light-secondary',
        // Lowercase versions
        unsure: 'light-warning',
        unavailable: 'light-secondary',
      }
      return variantMap[status] || 'light-secondary'
    },
    getTaskStatusIcon(status) {
      const iconMap = {
        pending: 'ClockIcon',
        processing: 'RefreshCwIcon',
        completed: 'CheckCircleIcon',
        approved: 'CheckCircleIcon',
        rejected: 'XCircleIcon',
        failed: 'AlertTriangleIcon',
        // Pre-review material statuses
        APPROVED: 'CheckCircleIcon',
        REJECTED: 'XCircleIcon',
        UNSURE: 'HelpCircleIcon',
        UNAVAILABLE: 'MinusCircleIcon',
        // Lowercase versions
        unsure: 'HelpCircleIcon',
        unavailable: 'MinusCircleIcon',
      }
      return iconMap[status] || 'HelpCircleIcon'
    },
    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString()
    },
    formatTime(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleTimeString()
    },
    isVideoMaterial(material) {
      if (!material) return false

      // Check material type
      const materialType = material.material_type || material.type || ''
      if (materialType.toLowerCase().includes('video')) {
        return true
      }

      // Check file extension from URL or content
      const contentUrl = material.content || material.url || material.creative_id || ''
      const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.mkv']
      return videoExtensions.some(ext => contentUrl.toLowerCase().includes(ext))
    },
    getCreativeThumbnailUrl(material) {
      if (!material) return ''

      // Only process IMAGE materials
      if (material.type !== 'IMAGE' && material.material_type !== 'IMAGE') {
        return ''
      }

      // If content is a direct URL (from TikTok CDN), use it directly
      if (material.content && (material.content.startsWith('http://') || material.content.startsWith('https://'))) {
        return material.content
      }

      // For numeric creative IDs, use the API endpoint
      const creativeId = material.creative_id
      if (creativeId && /^\d+$/.test(creativeId.toString())) {
        const apiUrl = `/api/creative/image/${creativeId}`
        return apiUrl
      }

      return ''
    },
    handleImageError(event) {
      // Replace with a simple placeholder instead of hiding
      const placeholderUrl = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA2NCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjY0IiBoZWlnaHQ9IjY0IiByeD0iOCIgZmlsbD0iI0Y4RjlGQSIgc3Ryb2tlPSIjRTJFOEYwIiBzdHJva2Utd2lkdGg9IjIiLz4KPGRhdGggZD0iTTE2IDQwTDI0IDMyTDMyIDQwTDQ4IDI0VjQ4SDE2VjQwWiIgZmlsbD0iIzlDQTNBRiIvPgo8Y2lyY2xlIGN4PSIyNCIgY3k9IjI0IiByPSI0IiBmaWxsPSIjOUNBM0FGIi8+Cjwvc3ZnPgo='
      const { target } = event
      target.src = placeholderUrl
      target.alt = 'Image not available'
      target.title = 'Image temporarily unavailable (CDN restriction)'
    },
    getStatusIconColor(status) {
      const colorMap = {
        APPROVED: 'text-success',
        REJECTED: 'text-danger',
        UNSURE: 'text-warning',
        UNAVAILABLE: 'text-secondary',
        approved: 'text-success',
        rejected: 'text-danger',
        unsure: 'text-warning',
        unavailable: 'text-secondary',
        completed: 'text-success',
        processing: 'text-info',
        pending: 'text-warning',
        failed: 'text-danger',
      }
      return colorMap[status] || 'text-secondary'
    },
    getVisualMaterials(materials) {
      // Filter materials to only show IMAGE and VIDEO types
      if (!materials) return []
      return materials.filter(material => {
        const materialType = (material.material_type || material.type || '').toUpperCase()
        return materialType === 'IMAGE' || materialType === 'VIDEO'
      })
    },
    hasTextMaterials(materials) {
      // Check if there are any text-based materials (AD_TEXT, LANDING_PAGE_URL)
      if (!materials) return false
      return materials.some(material => {
        const materialType = (material.material_type || material.type || '').toUpperCase()
        return materialType === 'AD_TEXT' || materialType === 'LANDING_PAGE_URL'
      })
    },
    getTextMaterialsCount(materials) {
      // Count text-based materials
      if (!materials) return 0
      return materials.filter(material => {
        const materialType = (material.material_type || material.type || '').toUpperCase()
        return materialType === 'AD_TEXT' || materialType === 'LANDING_PAGE_URL'
      }).length
    },
    getTextMaterialsTooltip(materials) {
      // Generate tooltip for text materials
      if (!materials) return ''
      const textMaterials = materials.filter(material => {
        const materialType = (material.material_type || material.type || '').toUpperCase()
        return materialType === 'AD_TEXT' || materialType === 'LANDING_PAGE_URL'
      })
      const types = textMaterials.map(m => {
        const type = (m.material_type || m.type || '').toUpperCase()
        if (type === 'AD_TEXT') return 'Ad Text'
        if (type === 'LANDING_PAGE_URL') return 'Landing Page'
        return type
      })
      return `Includes: ${types.join(', ')}`
    },
    getMaterialsSummary(materials) {
      if (!materials || materials.length === 0) return []

      // Group materials by type and status
      const grouped = {}

      materials.forEach(material => {
        const type = (material.material_type || material.type || '').toUpperCase()
        const status = material.pre_review_status || material.status || 'pending'

        // Determine display type
        let displayType = ''
        if (type === 'VIDEO') displayType = 'video'
        else if (type === 'IMAGE') displayType = 'image'
        else if (type === 'AD_TEXT') displayType = 'ad text'
        else if (type === 'LANDING_PAGE_URL') displayType = 'landing page'
        else displayType = type.toLowerCase()

        const key = `${displayType}_${status}`

        if (!grouped[key]) {
          grouped[key] = {
            type: displayType,
            status,
            count: 0,
          }
        }
        grouped[key].count += 1
      })

      // Convert to array and sort
      return Object.values(grouped).sort((a, b) => {
        // Sort by type first (video, image, ad text, landing page)
        const typeOrder = ['video', 'image', 'ad text', 'landing page']
        const aIndex = typeOrder.indexOf(a.type)
        const bIndex = typeOrder.indexOf(b.type)

        if (aIndex !== bIndex) {
          if (aIndex === -1) return 1
          if (bIndex === -1) return -1
          return aIndex - bIndex
        }

        // Then by status
        return a.status.localeCompare(b.status)
      })
    },
  },
}
</script>

<style scoped>
.creative-thumbnail-small {
  width: 80px;
  height: 80px;
  display: inline-block;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.creative-thumbnail-small img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.placeholder-thumbnail,
.video-thumbnail {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f8f9fa;
}

.placeholder-thumbnail small {
  font-size: 10px;
  margin-top: 4px;
}

.text-materials-indicator {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px;
  background: #f8f9fa;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
}

.materials-summary {
  font-size: 0.875rem;
}

.material-line {
  margin-bottom: 0.25rem;
  display: flex;
  align-items: center;
}

.material-count {
  color: #6c757d;
  margin-right: 0.25rem;
}
</style>
